import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Package, 
  Search, 
  Filter,
  Image as ImageIcon,
  AlertCircle,
  AlertTriangle,
  X
} from 'lucide-react';
import AddProductModal from '../AddProductModal';
import UpdateProductModal from '../UpdateProductModal';
import Alert from '../Alert';
import { getProductImageUrl as getConfigProductImageUrl, API_BASE_URL } from '../../config';

// Utility function to safely render HTML content
const renderHtmlContent = (html) => {
  if (!html) return '';
  return html;
};

const Products = ({ authToken }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [imageRefreshKey, setImageRefreshKey] = useState(0); // For force refresh images
  
  // Alert state management
  const [alert, setAlert] = useState({
    show: false,
    type: 'info',
    title: '',
    message: ''
  });

  // Alert helper functions
  const showAlert = (type, title, message) => {
    setAlert({
      show: true,
      type,
      title,
      message
    });
  };

  const hideAlert = () => {
    setAlert({
      show: false,
      type: 'info',
      title: '',
      message: ''
    });
  };

  // Get auth token from localStorage
  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
      'Authorization': `Bearer ${token}`,
    };
  };

  // Fetch products from API
  const fetchProducts = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/fetchproducts`, {
        headers: getAuthHeaders(),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setProducts(data.products || []);
      } else {
        setError(data.error || 'Failed to fetch products');
      }
    } catch (err) {
      setError('Network error. Please check if server is running.');
    } finally {
      setLoading(false);
    }
  };

  // Delete single product permanently (hard delete)
  const deleteProduct = async (productId) => {
    // Show confirmation alert
    showAlert('warning', 'Confirm Deletion', 'Are you sure you want to permanently delete this product? This action cannot be undone and will remove all product images.');
    
    // We'll handle the actual deletion in a separate function
    setSelectedProduct(products.find(p => p.product_id === productId));
  };

  // Delete multiple products permanently (hard delete)
  const deleteSelectedProducts = async () => {
    if (selectedProducts.length === 0) return;
    
    const productCount = selectedProducts.length;
    const message = productCount === 1 
      ? 'Are you sure you want to permanently delete this product? This action cannot be undone and will remove all product images.'
      : `Are you sure you want to permanently delete ${productCount} products? This action cannot be undone and will remove all product images.`;
    
    showAlert('warning', 'Confirm Bulk Deletion', message);
  };

  // Confirm and execute single product deletion
  const confirmDeleteProduct = async () => {
    if (!selectedProduct) return;
    
    const productId = selectedProduct.product_id;
    
    try {
      const response = await fetch(`${API_BASE_URL}/hardDeleteProduct`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ product_id: productId }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setProducts(products.filter(p => p.product_id !== productId));
        setSelectedProducts(selectedProducts.filter(id => id !== productId));
        showAlert('success', 'Success', 'Product permanently deleted successfully');
        setSelectedProduct(null);
      } else {
        showAlert('error', 'Error', data.error || 'Failed to delete product');
      }
    } catch (err) {
      showAlert('error', 'Error', 'Error deleting product');
      console.error('Delete error:', err);
    }
  };

  // Confirm and execute bulk product deletion
  const confirmBulkDeleteProducts = async () => {
    if (selectedProducts.length === 0) return;
    
    const productIds = [...selectedProducts]; // Create a copy to avoid mutation during loop
    let successCount = 0;
    let errorCount = 0;
    const errors = [];
    
    // Loop through all selected products and delete them one by one
    for (const productId of productIds) {
      try {
        const response = await fetch(`${API_BASE_URL}/hardDeleteProduct`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          body: JSON.stringify({ product_id: productId }),
        });

        const data = await response.json();

        if (response.ok && data.success) {
          successCount++;
        } else {
          errorCount++;
          const product = products.find(p => p.product_id === productId);
          errors.push(`${product?.name || productId}: ${data.error || 'Failed to delete'}`);
        }
      } catch (err) {
        errorCount++;
        const product = products.find(p => p.product_id === productId);
        errors.push(`${product?.name || productId}: Network error`);
        console.error('Delete error for product', productId, ':', err);
      }
    }
    
    // Update the products list by removing successfully deleted products
    setProducts(products.filter(p => !productIds.includes(p.product_id)));
    setSelectedProducts([]); // Clear selection
    
    // Show appropriate success/error message
    if (successCount > 0 && errorCount === 0) {
      showAlert('success', 'Success', `${successCount} product${successCount > 1 ? 's' : ''} deleted successfully`);
    } else if (successCount > 0 && errorCount > 0) {
      showAlert('warning', 'Partial Success', `${successCount} product${successCount > 1 ? 's' : ''} deleted, ${errorCount} failed. Check console for details.`);
      console.error('Failed deletions:', errors);
    } else {
      showAlert('error', 'Error', `Failed to delete ${errorCount} product${errorCount > 1 ? 's' : ''}. Check console for details.`);
      console.error('Failed deletions:', errors);
    }
  };

  // Get product image URL with cache busting
  const getProductImageUrl = (product) => {
    const baseUrl = getConfigProductImageUrl(product);
    return baseUrl ? `${baseUrl}&refresh=${imageRefreshKey}` : null;
  };

  // Handle image load error - show placeholder
  const handleImageError = (event) => {
    event.target.style.display = 'none';
    const placeholder = event.target.nextSibling;
    if (placeholder) {
      placeholder.style.display = 'block';
    }
  };

  // Handle product updated successfully
  const handleProductUpdated = () => {
    setImageRefreshKey(prev => prev + 1); // Force refresh images
    fetchProducts(); // Refresh the products list
    setShowEditModal(false); // Close the modal
    showAlert('success', 'Success', 'Product updated successfully');
  };

  // Handle product added successfully
  const handleProductAdded = () => {
    setImageRefreshKey(prev => prev + 1); // Force refresh images
    fetchProducts();
    showAlert('success', 'Success', 'Product added successfully');
  };

  // Handle select all products
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedProducts(filteredProducts.map(p => p.product_id));
    } else {
      setSelectedProducts([]);
    }
  };

  // Handle select individual product
  const handleSelectProduct = (productId, checked) => {
    if (checked) {
      setSelectedProducts([...selectedProducts, productId]);
    } else {
      setSelectedProducts(selectedProducts.filter(id => id !== productId));
    }
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  // Get sort indicator
  const getSortIndicator = (field) => {
    if (sortBy !== field) return null;
            return sortOrder === 'asc' ? ' (asc)' : ' (desc)';
  };

  // Filter and sort products
  const filteredProducts = products
    .filter(product => {
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           product.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || product.status === statusFilter;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      
      // Handle different data types for sorting
      if (sortBy === 'base_price' || sortBy === 'sale_price' || sortBy === 'quantity') {
        // Numeric sorting
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      } else if (sortBy === 'created_at') {
        // Date sorting
        aValue = new Date(aValue || 0);
        bValue = new Date(bValue || 0);
      } else {
        // String sorting
        aValue = String(aValue || '').toLowerCase();
        bValue = String(bValue || '').toLowerCase();
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });

  // Load products on component mount
  useEffect(() => {
    fetchProducts();
  }, []);

  // Get status badge style
  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-red-100 text-red-800',
      draft: 'bg-yellow-100 text-yellow-800'
    };
    return styles[status] || styles.draft;
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Products</h1>
          <p className="text-gray-600">
            {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'}
            {selectedProducts.length > 0 && ` - ${selectedProducts.length} selected`}
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          <span>Add Product</span>
        </button>
      </div>

      {/* Filters and Actions */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            <div className="relative min-w-0 flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white min-w-[120px]"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="draft">Draft</option>
              </select>
            </div>
          </div>

          {/* Bulk Actions */}
          {selectedProducts.length > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">{selectedProducts.length} selected</span>
              <button 
                onClick={deleteSelectedProducts}
                className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
              >
                Delete Selected
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* Products Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {loading ? (
          // Loading State
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading products...</p>
          </div>
        ) : filteredProducts.length > 0 ? (
          // Products Table
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              {/* Table Header */}
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedProducts.length === filteredProducts.length && filteredProducts.length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                    onClick={() => handleSort('name')}
                  >
                    Product{getSortIndicator('name')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                    onClick={() => handleSort('status')}
                  >
                    Status{getSortIndicator('status')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                    onClick={() => handleSort('quantity')}
                  >
                    Inventory{getSortIndicator('quantity')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                    onClick={() => handleSort('base_price')}
                  >
                    Price{getSortIndicator('base_price')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                    onClick={() => handleSort('sku')}
                  >
                    SKU{getSortIndicator('sku')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                    onClick={() => handleSort('created_at')}
                  >
                    Created{getSortIndicator('created_at')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>

              {/* Table Body */}
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredProducts.map((product) => (
                  <tr key={product.product_id} className="hover:bg-gray-50 transition-colors">
                    {/* Checkbox */}
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedProducts.includes(product.product_id)}
                        onChange={(e) => handleSelectProduct(product.product_id, e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>

                    {/* Product Info */}
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-4">
                        {/* Product Image */}
                        <div className="flex-shrink-0 w-12 h-12">
                          <img
                            src={getProductImageUrl(product)}
                            alt={product.name}
                            className="w-12 h-12 object-cover rounded-lg border"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <div className="w-12 h-12 bg-gray-100 rounded-lg hidden items-center justify-center border">
                            <ImageIcon className="h-6 w-6 text-gray-400" />
                          </div>
                        </div>
                        
                        {/* Product Details */}
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {product.name}
                          </div>
                          <div 
                            className="text-sm text-gray-500 max-w-xs overflow-hidden"
                            style={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                            dangerouslySetInnerHTML={{ 
                              __html: renderHtmlContent(product.description) || 'No description' 
                            }}
                          />
                        </div>
                      </div>
                    </td>

                    {/* Status */}
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(product.status)}`}>
                        {product.status.charAt(0).toUpperCase() + product.status.slice(1)}
                      </span>
                    </td>

                    {/* Inventory */}
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {product.quantity} in stock
                      </div>
                      <div className="text-sm text-gray-500">
                        {product.quantity === 0 ? 'Out of stock' : 
                         product.quantity < 10 ? 'Low stock' : 'In stock'}
                      </div>
                    </td>

                    {/* Price */}
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        {product.sale_price ? (
                          <div>
                            <div className="font-medium text-gray-900">
                              ₹{parseFloat(product.sale_price).toFixed(2)}
                            </div>
                            <div className="text-gray-500 line-through text-xs">
                              ₹{parseFloat(product.base_price).toFixed(2)}
                            </div>
                          </div>
                        ) : (
                          <div className="font-medium text-gray-900">
                            ₹{parseFloat(product.base_price).toFixed(2)}
                          </div>
                        )}
                      </div>
                    </td>

                    {/* SKU */}
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 font-mono">
                        {product.sku || '-'}
                      </div>
                    </td>

                    {/* Created Date */}
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {product.created_at ? new Date(product.created_at).toLocaleDateString('en-IN', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        }) : '-'}
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => {
                            setSelectedProduct(product);
                            setShowEditModal(true);
                          }}
                          className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                          title="Edit product"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        
                        <button
                          onClick={() => deleteProduct(product.product_id)}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                          title="Delete product"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          // Empty State
          <div className="text-center py-12">
            <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || statusFilter !== 'all' 
                ? 'Try adjusting your filters or search terms'
                : 'Get started by adding your first product'
              }
            </p>
            {!searchTerm && statusFilter === 'all' && (
              <button
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>Add Your First Product</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Add Product Modal */}
      <AddProductModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onProductAdded={handleProductAdded}
      />

      {/* Update Product Modal */}
      <UpdateProductModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onProductUpdated={handleProductUpdated}
        product={selectedProduct}
      />

      {/* Alert Component */}
      <Alert
        show={alert.show}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={hideAlert}
        autoClose={alert.type === 'success' || alert.type === 'error'}
        autoCloseDelay={3000}
      />

      {/* Delete Confirmation Dialog */}
      {alert.show && alert.type === 'warning' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertTriangle className="h-6 w-6 text-yellow-500 mr-3" />
              <h3 className="text-lg font-medium text-gray-900">{alert.title}</h3>
            </div>
            <p className="text-gray-600 mb-6">{alert.message}</p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={hideAlert}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  hideAlert();
                  // Check if this is a bulk deletion or single deletion
                  if (alert.title === 'Confirm Bulk Deletion') {
                    confirmBulkDeleteProducts();
                  } else {
                    confirmDeleteProduct();
                  }
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;